import { Inject, Pipe } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { PipeTransform } from '@angular/core';

export type UnitOfTime = 'seconds' | 'minutes' | 'hours';

interface DurationPipeParameters {
  truncateTo?: UnitOfTime;
  useWords?: boolean;
}

/**
 * Formats milliseconds into a time string
 * For example:
 * converts 109000 -> 1:49
 */
@Pipe({
  name: 'tdWbDuration'
})
export class DurationPipe implements PipeTransform {

  constructor(@Inject(TranslateService) private translateService: TranslateService) {
  }

  transform(value: number | undefined, params?: DurationPipeParameters): string {
    if (typeof value === 'number' && value > 0) {
      const milliseconds = Math.floor(value % 1000);
      const seconds = Math.floor((value / 1000) % 60);
      const minutes = Math.floor((value / (1000 * 60)) % 60);
      const hours = Math.floor((value / (1000 * 60 * 60)) % 24);
      const parameters = params !== undefined ? params : {};
      // indication if the string contains any values yet or not

      if (parameters.useWords) {
        return this.transformWithWords(parameters, hours, minutes, seconds, milliseconds);
      } else {
        return this.transformWithNumbers(parameters, hours, minutes, seconds, milliseconds);
      }
    }
    return '';  // return empty string if no value provided
  }

  private transformWithWords(parameters: DurationPipeParameters,
                             hours: number, minutes: number, seconds: number, milliseconds: number): string {
    let returnString = '';
    if (hours > 0) {
      returnString = hours.toString() + ' ' + this.translateService.instant('tdWb.core.pipes.duration.HOURS');
    }
    if (parameters.truncateTo === 'hours') {
      return returnString;
    }

    if (minutes > 0) {
      if (hours > 0) {
        returnString = returnString + ' ';
      }
      returnString = returnString + minutes.toString() + ' ' + this.translateService.instant('tdWb.core.pipes.duration.MINUTES');
    }
    if (parameters.truncateTo === 'minutes') {
      return returnString;
    }

    if (seconds > 0) {
      if (hours > 0 || minutes > 0) {
        returnString = returnString + ' ';
      }
      returnString = returnString + seconds.toString() + ' ' + this.translateService.instant('tdWb.core.pipes.duration.SECONDS');
    }
    if (parameters.truncateTo === 'seconds') {
      return returnString;
    }

    if (milliseconds > 0) {
      if (hours > 0 || minutes > 0 || seconds > 0) {
        returnString = returnString + ' ';
      }
      returnString = returnString + milliseconds.toString() + ' ' + this.translateService.instant('tdWb.core.pipes.duration.MILLISECONDS');
    }
    return returnString;
  }

  private transformWithNumbers(parameters: DurationPipeParameters,
                               hours: number, minutes: number, seconds: number, milliseconds: number): string {
    let numberSet = false;
    let returnString = '';
    if (hours > 0) {
      returnString = hours.toString();
      numberSet = true;
      if (parameters.truncateTo === 'hours') {
        return returnString;
      }
    }
    if (minutes > 0 || seconds > 0 || numberSet) {
      returnString = this.addColonIfNeeded(returnString);
      returnString = numberSet ? returnString + this.padNumberLeftWithZeros(minutes, 2) : returnString + minutes.toString();
      numberSet = true;
      if (parameters.truncateTo === 'minutes') {
        return returnString;
      }
    }
    if (seconds > 0 || numberSet) {
      returnString = this.addColonIfNeeded(returnString);
      returnString = numberSet ? returnString + this.padNumberLeftWithZeros(seconds, 2) : returnString + seconds.toString();
      numberSet = true;
      if (parameters.truncateTo === 'seconds') {
        return returnString;
      }
    }
    if (milliseconds > 0 || numberSet) {
      returnString = this.addColonIfNeeded(returnString);
      returnString = returnString + this.padNumberLeftWithZeros(milliseconds, 3);
    }
    return returnString;
  }

  private addColonIfNeeded(value: string): string {
    return value !== undefined && value.length > 0 ? value + ':' : value;
  }

  private padNumberLeftWithZeros(rawNumber: number, numberOfDigits: number): string {
    let paddedNumber: string = rawNumber + '';
    while (paddedNumber.length < numberOfDigits) {
      paddedNumber = '0' + paddedNumber;
    }
    return paddedNumber;
  }
}
